import { themeById, type ShowInvite } from '@runway/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHub } from '../../state/hub';
import { useSession } from '../../state/session';
import { Button } from '../../ui/Button';
import { toast } from '../../ui/Toast';
import { UserAvatar } from '../friends/UserAvatar';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * Pending invitations addressed to me, with Join / Decline. Used by the bell
 * popover in the shell and the Invitations card on Home.
 */
export function InvitesList({ compact = false, onNavigate }: { compact?: boolean; onNavigate?: () => void }) {
  const me = useSession((s) => s.user?.id);
  const invites = useHub((s) => s.invites);
  const respondInvite = useHub((s) => s.respondInvite);
  const loaded = useHub((s) => s.loaded.invites);
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);

  const pending = invites.filter((i) => i.status === 'pending' && i.to.id === me).sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const respond = async (inv: ShowInvite, accept: boolean) => {
    setBusy(inv.id);
    try {
      await respondInvite(inv.id, accept);
      if (accept) {
        onNavigate?.();
        navigate(`/show/${inv.showId}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  if (pending.length === 0) {
    return (
      <div className="empty">
        <div className="empty-emoji" aria-hidden="true">
          🎟️
        </div>
        <h4>No invitations right now</h4>
        <p>{loaded ? 'When a friend invites you to a show, it shows up here.' : 'Checking for invites…'}</p>
      </div>
    );
  }

  return (
    <div>
      {pending.map((inv) => (
        <div key={inv.id} className="invite-row">
          <UserAvatar displayName={inv.from.displayName} picture={inv.from.picture} avatar={inv.from.avatar} size={compact ? 'sm' : 'md'} />
          <div className="invite-row-body">
            <strong>{inv.from.displayName}</strong>
            <span className="small dim">
              {inv.theme ? `“${themeById(inv.theme)?.title ?? inv.theme}”` : 'A fashion show'} · {timeAgo(inv.createdAt)}
            </span>
          </div>
          <div className="invite-row-actions">
            <Button size="sm" variant="gold" loading={busy === inv.id} onClick={() => void respond(inv, true)}>
              Join
            </Button>
            <Button size="sm" variant="ghost" disabled={busy === inv.id} onClick={() => void respond(inv, false)} aria-label="Decline">
              ✕
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
